import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Package, Users, Shield, CheckCircle, MessageCircle, Truck, Clock, Phone } from "lucide-react";
import { useState } from "react";
import { CityAutocomplete } from "@/components/ui/city-autocomplete";

const EnvoyerColisMarocPasCher = () => {
  const navigate = useNavigate();
  const [departureCity, setDepartureCity] = useState("");
  const [arrivalCity, setArrivalCity] = useState("");
  const [packageType, setPackageType] = useState("");
  const [preferredDate, setPreferredDate] = useState("");

  const handleQuickFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Store form data in localStorage for the full form
    const formData = {
      departure_city: departureCity,
      arrival_city: arrivalCity,
      package_type: packageType,
      preferred_date: preferredDate,
    };
    localStorage.setItem("transport_request_draft", JSON.stringify(formData));
    navigate("/demande-transport");
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Bonjour, je souhaite envoyer un colis au Maroc et j'aimerais avoir plus d'informations.");
    window.open(`https://wa.me/33624570308?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Envoyer un Colis au Maroc – Transport Fiable France ⇄ Maroc | GoBledPro</title>
        <meta 
          name="description" 
          content="Envoyez un colis au Maroc simplement et en toute confiance. GoBledPro vous met en relation avec des transporteurs vérifiés pour vos envois France ⇄ Maroc. Demande gratuite, réponse rapide." 
        />
        <link rel="canonical" href="https://gobledpro.com/envoyer-colis-maroc-pas-cher" />
        <meta property="og:title" content="Envoyer un Colis au Maroc – Transport Fiable France ⇄ Maroc | GoBledPro" />
        <meta property="og:description" content="Envoyez un colis au Maroc simplement et en toute confiance. GoBledPro vous met en relation avec des transporteurs vérifiés." />
        <meta property="og:image" content="https://gobledpro.com/og-envoyer-colis.png" />
        <meta property="og:url" content="https://gobledpro.com/envoyer-colis-maroc-pas-cher" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="GoBledPro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://gobledpro.com/og-envoyer-colis.png" />
      </Helmet>

      <Header />

      <main className="flex-1">
        {/* HERO SECTION - Above the fold */}
        <section className="pt-24 pb-12 md:pt-28 md:pb-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6 leading-tight">
                Envoyer un colis au Maroc simplement et en toute confiance
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Trouvez rapidement un transporteur fiable pour vos envois France ⇄ Maroc, sans stress ni mauvaises surprises.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button asChild size="lg" className="gap-2 text-base px-8 py-6 hover:scale-[1.02] transition-transform">
                  <Link to="/demande-transport">
                    Demander un transport
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="gap-2 text-base px-8 py-6 border-green-600 text-green-600 hover:bg-green-50 hover:text-green-700"
                  onClick={handleWhatsAppClick}
                >
                  <MessageCircle className="w-5 h-5" />
                  Besoin d'une réponse rapide ?
                </Button>
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                Mise en relation gratuite · Transporteurs vérifiés · Réponse sous 24h
              </p>
            </div>
          </div>
        </section>

        {/* COMMENT ÇA MARCHE - 3 étapes */}
        <section className="py-12 md:py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground text-center mb-10">
                Comment ça marche ?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">1</div>
                  <h3 className="font-semibold text-foreground mb-2">Décrivez votre colis</h3>
                  <p className="text-muted-foreground text-sm">
                    Ville de départ, ville d'arrivée, type de colis et date souhaitée.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Truck className="w-8 h-8 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">2</div>
                  <h3 className="font-semibold text-foreground mb-2">GoBledPro transmet votre demande</h3>
                  <p className="text-muted-foreground text-sm">
                    Votre demande est envoyée à des transporteurs vérifiés qui effectuent régulièrement le trajet.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">3</div>
                  <h3 className="font-semibold text-foreground mb-2">Vous êtes mis en relation</h3>
                  <p className="text-muted-foreground text-sm">
                    Vous recevez rapidement des propositions et choisissez le transporteur qui vous convient.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTIONS SEO - Intentions secondaires */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">
              
              {/* H2: Envoyer un colis au Maroc pas cher */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Envoyer un colis au Maroc pas cher
                </h2>
                <p className="text-muted-foreground mb-4">
                  Chercher le meilleur prix pour envoyer un colis au Maroc est naturel. Mais attention : le moins cher n'est pas toujours le plus fiable. Un tarif anormalement bas peut cacher des frais supplémentaires ou un service sans garantie.
                </p>
                <p className="text-muted-foreground mb-4">
                  Avec GoBledPro, vous accédez à des transporteurs qui mutualisent les envois sur un même trajet. Cette approche permet de réduire les coûts tout en conservant un service encadré et sécurisé. Vous obtenez ainsi un bon rapport qualité-prix sans prendre de risques.
                </p>
                <p className="text-muted-foreground">
                  Pour optimiser vos coûts : soyez flexible sur les dates, évitez les périodes de pointe (été, Ramadan, fêtes) et préparez un colis compact.
                </p>
              </div>

              {/* H2: Envoyer un colis au Maroc entre particuliers */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Envoyer un colis au Maroc entre particuliers
                </h2>
                <p className="text-muted-foreground mb-4">
                  Les envois entre particuliers sont courants pour la diaspora marocaine : cadeaux pour la famille, affaires personnelles, produits difficiles à trouver au Maroc. Le besoin est réel, mais les solutions informelles (groupes Facebook, connaissances de connaissances) comportent des risques.
                </p>
                <p className="text-muted-foreground mb-4">
                  GoBledPro vous permet d'envoyer vos colis comme vous le feriez avec un proche, mais avec l'assurance de traiter avec un transporteur vérifié. Vous gardez le contact humain tout en bénéficiant d'un cadre structuré.
                </p>
                <p className="text-muted-foreground">
                  Que ce soit pour des vêtements, des appareils électroniques, des denrées alimentaires ou des documents importants, vous êtes mis en relation avec des professionnels qui connaissent les spécificités de ce trajet.
                </p>
              </div>

              {/* H2: Transport de bagages et valises vers le Maroc */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Transport de bagages et valises vers le Maroc
                </h2>
                <p className="text-muted-foreground mb-4">
                  Vous avez des bagages volumineux à envoyer au Maroc ? Valises, cartons, effets personnels pour un déménagement partiel ou un retour au pays : les transporteurs GoBledPro acceptent également les envois de bagages.
                </p>
                <p className="text-muted-foreground mb-4">
                  Contrairement aux compagnies aériennes qui facturent très cher les excédents, le transport routier permet d'envoyer de gros volumes à des tarifs plus accessibles. Et sans limite de poids par valise.
                </p>
                <p className="text-muted-foreground">
                  <Link to="/transport-bagages-france-maroc" className="text-primary hover:underline">
                    Découvrez notre guide complet sur le transport de bagages France ⇄ Maroc →
                  </Link>
                </p>
              </div>

              {/* H2: Pourquoi passer par GoBledPro */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Pourquoi passer par GoBledPro plutôt qu'un numéro trouvé au hasard ?
                </h2>
                <p className="text-muted-foreground mb-4">
                  Sur les réseaux sociaux, vous trouverez des dizaines de numéros de téléphone de "transporteurs" ou "voyageurs". Mais sans vérification, vous n'avez aucune garantie sur leur fiabilité, leur assurance ou même leur existence réelle.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 my-6">
                  <div className="flex items-start gap-3 p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <span className="text-destructive font-bold">✗</span>
                    <div>
                      <span className="font-medium text-foreground">Numéro au hasard</span>
                      <p className="text-sm text-muted-foreground mt-1">Pas de vérification, pas de recours, pas de suivi</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                    <span className="text-primary font-bold">✓</span>
                    <div>
                      <span className="font-medium text-foreground">Via GoBledPro</span>
                      <p className="text-sm text-muted-foreground mt-1">Transporteurs vérifiés, contact encadré, suivi possible</p>
                    </div>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  GoBledPro n'est pas un transporteur : nous sommes une plateforme de mise en relation. Notre rôle est de vous connecter avec des professionnels sérieux, tout en facilitant les échanges et la confiance.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* SECTION CONFIANCE */}
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground text-center mb-10">
                Un cadre de confiance pour vos envois
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Transporteurs vérifiés</h3>
                  <p className="text-muted-foreground text-sm">
                    Identité, documents et assurance contrôlés
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Contact humain</h3>
                  <p className="text-muted-foreground text-sm">
                    Un interlocuteur joignable, pas un robot
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Mise en relation rapide</h3>
                  <p className="text-muted-foreground text-sm">
                    Réponse sous 24 à 48h maximum
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Pas d'intermédiaire opaque</h3>
                  <p className="text-muted-foreground text-sm">
                    Vous traitez directement avec le transporteur
                  </p>
                </div>
              </div>

              {/* Micro-preuves */}
              <div className="mt-10 flex flex-wrap justify-center gap-8 text-center">
                <div>
                  <p className="text-2xl font-bold text-foreground">Demandes réelles</p>
                  <p className="text-sm text-muted-foreground">traitées chaque semaine</p>
                </div>
                <div className="hidden sm:block w-px bg-border"></div>
                <div>
                  <p className="text-2xl font-bold text-foreground">Transporteurs actifs</p>
                  <p className="text-sm text-muted-foreground">France ⇄ Maroc</p>
                </div>
                <div className="hidden sm:block w-px bg-border"></div>
                <div>
                  <p className="text-2xl font-bold text-foreground">Réponse rapide</p>
                  <p className="text-sm text-muted-foreground">sous 24h en moyenne</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FORMULAIRE RAPIDE */}
        <section className="py-12 md:py-16" id="formulaire">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-heading font-bold text-foreground text-center mb-2">
                  Recevoir des propositions de transport
                </h2>
                <p className="text-muted-foreground text-center mb-8">
                  Remplissez ce formulaire, nous transmettons votre demande aux transporteurs disponibles.
                </p>

                <form onSubmit={handleQuickFormSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="departure_city">Ville de départ (France)</Label>
                      <CityAutocomplete
                        value={departureCity}
                        onChange={setDepartureCity}
                        placeholder="Ex: Paris, Lyon, Marseille..."
                        country="France"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="arrival_city">Ville d'arrivée (Maroc)</Label>
                      <CityAutocomplete
                        value={arrivalCity}
                        onChange={setArrivalCity}
                        placeholder="Ex: Casablanca, Rabat, Fès..."
                        country="Maroc"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="package_type">Type de colis / bagage</Label>
                      <Select value={packageType} onValueChange={setPackageType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="colis">Colis (carton)</SelectItem>
                          <SelectItem value="valise">Valise / Bagage</SelectItem>
                          <SelectItem value="electromenager">Électroménager</SelectItem>
                          <SelectItem value="meuble">Meuble</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferred_date">Date souhaitée</Label>
                      <Input
                        id="preferred_date"
                        type="date"
                        value={preferredDate}
                        onChange={(e) => setPreferredDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full gap-2 text-base py-6 hover:scale-[1.01] transition-transform">
                    Recevoir des propositions de transport
                    <ArrowRight className="w-5 h-5" />
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Gratuit et sans engagement. Vos données sont protégées.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* LIENS INTERNES SEO */}
        <section className="py-12 bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                En savoir plus
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link 
                  to="/transport-colis-france-maroc" 
                  className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors group"
                >
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                    Transport de colis France ⇄ Maroc
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Le guide complet</p>
                </Link>
                <Link 
                  to="/transport-bagages-france-maroc" 
                  className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors group"
                >
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                    Transport de bagages et valises
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Envoyez vos effets personnels</p>
                </Link>
                <Link 
                  to="/transporteurs-verifies-maroc" 
                  className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors group"
                >
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                    Transporteurs vérifiés
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Notre processus de vérification</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
              Prêt à envoyer votre colis au Maroc ?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Décrivez votre envoi et recevez des propositions de transporteurs vérifiés, sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="gap-2 text-base px-8 py-6 hover:scale-[1.02] transition-transform">
                <Link to="/demande-transport">
                  Demander un transport
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="gap-2 text-base px-8 py-6"
                onClick={handleWhatsAppClick}
              >
                <MessageCircle className="w-5 h-5" />
                Contacter par WhatsApp
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EnvoyerColisMarocPasCher;
